import { type CartScope, lineSubtotal, type CartLine } from "@/lib/cart-store";
import type { MenuSection, PublicMenuProduct, PublicMenuResponse } from "@/types/menu";

export type FulfillmentType = "dine-in" | "takeaway" | "delivery";

export type PublicMenuFlowVariant = FulfillmentType;

export type PublicMenuFlowDeliveryInput = {
  currency: string;
  deliveryMinimumOrder: number;
  deliveryFeeFlat: number;
  cashOnDeliveryAvailable: boolean;
  payNowAvailable: boolean;
};

type DineInFlowInput = {
  variant: "dine-in";
  menu: PublicMenuResponse;
  cartLines: CartLine[];
  orgSlug: string | undefined;
  qrToken: string | null;
  tableLabel: string | null;
  guestError: string | null;
};

type TakeawayFlowInput = {
  variant: "takeaway";
  menu: PublicMenuResponse;
  cartLines: CartLine[];
  orgSlug: string | undefined;
};

type DeliveryFlowInput = {
  variant: "delivery";
  menu: PublicMenuResponse;
  cartLines: CartLine[];
  orgSlug: string | undefined;
  delivery: PublicMenuFlowDeliveryInput;
};

export type CreatePublicMenuFlowModelInput =
  | DineInFlowInput
  | TakeawayFlowInput
  | DeliveryFlowInput;

export type PublicMenuFlowCartRow = {
  kind: "subtotal" | "delivery-fee" | "total";
  amount: number;
  emphasized: boolean;
};

export type PublicMenuFlowCheckoutBlocker =
  | { kind: "minimum-order"; amount: number }
  | { kind: "no-payment-method" };

export type PublicMenuFlowSelector = {
  visible: boolean;
  enabledTypes: FulfillmentType[];
  current: FulfillmentType;
};

export type PublicMenuFlowModel = {
  variant: PublicMenuFlowVariant;
  organization: PublicMenuResponse["organization"];
  currency: string | null;
  productsBySection: Map<string, PublicMenuProduct[]>;
  quantitiesByProductId: Map<string, number>;
  sectionsForNav: MenuSection[];
  sectionsForList: MenuSection[];
  productOrderingEnabled: boolean;
  tableLabel: string | null;
  guestError: string | null;
  showScanToOrder: boolean;
  deliveryMinimumOrder: number | null;
  selector: PublicMenuFlowSelector;
  cart: {
    lines: CartLine[];
    itemCount: number;
    subtotal: number;
  };
  cartDrawer: {
    visible: boolean;
    checkoutTarget: string | null;
    canCheckout: boolean;
    rows: PublicMenuFlowCartRow[];
    blockers: PublicMenuFlowCheckoutBlocker[];
  };
};

export function isFulfillmentType(value: string | null | undefined): value is FulfillmentType {
  return value === "dine-in" || value === "takeaway" || value === "delivery";
}

export type OrgFulfillmentCapabilities = {
  businessType: "restaurant" | "supermarket";
  orderingEnabled: boolean;
  deliveryEnabled: boolean;
};

/**
 * The Fulfillment types an Organization exposes to customers. Restaurants are
 * table-bound (dine-in via QR); supermarkets are pickup-bound (takeaway). Both
 * can additionally offer delivery when `deliveryEnabled` is on.
 */
export function enabledFulfillmentTypes(caps: OrgFulfillmentCapabilities): FulfillmentType[] {
  const types: FulfillmentType[] = [];
  if (caps.orderingEnabled) {
    if (caps.businessType === "restaurant") {
      types.push("dine-in");
    } else {
      types.push("takeaway");
    }
  }
  if (caps.deliveryEnabled) {
    types.push("delivery");
  }
  return types;
}

/**
 * QR-scanned dine-in sessions skip the selector — the Fulfillment type is
 * implied by the table. Otherwise the selector shows when more than one
 * Fulfillment type is enabled for the Organization.
 */
export function shouldShowFulfillmentSelector(input: {
  enabledTypes: FulfillmentType[];
  isQrDineIn: boolean;
}): boolean {
  return !input.isQrDineIn && input.enabledTypes.length > 1;
}

/**
 * Resolves the effective Fulfillment type from the `fulfillment` URL query
 * param. QR → `dine-in`; an explicit, enabled param wins; otherwise the first
 * enabled type (the public-link default).
 */
export function deriveFulfillmentVariant(input: {
  fulfillmentParam: string | null;
  enabledTypes: FulfillmentType[];
  isQrDineIn: boolean;
}): FulfillmentType {
  if (input.isQrDineIn) {
    return "dine-in";
  }
  const { fulfillmentParam, enabledTypes } = input;
  if (
    fulfillmentParam &&
    isFulfillmentType(fulfillmentParam) &&
    enabledTypes.includes(fulfillmentParam)
  ) {
    return fulfillmentParam;
  }
  return enabledTypes[0] ?? "dine-in";
}

/**
 * Each Fulfillment type drives a distinct, independently-persisted cart scope.
 * Delivery uses its own scope; dine-in and takeaway share the default scope
 * (they are mutually exclusive access contexts — QR vs public link — so they
 * never collide, and switching between pickup and delivery swaps scopes with
 * no data loss).
 */
export function cartScopeForFulfillment(type: FulfillmentType): CartScope | undefined {
  if (type === "delivery") {
    return { mode: "delivery" };
  }
  return undefined;
}

export function fulfillmentTypeLabelKey(type: FulfillmentType): string {
  if (type === "dine-in") {
    return "delivery.fulfillment.dineIn";
  }
  if (type === "takeaway") {
    return "delivery.fulfillment.takeaway";
  }
  return "delivery.fulfillment.delivery";
}

function groupProductsBySection(products: PublicMenuProduct[]) {
  const map = new Map<string, PublicMenuProduct[]>();

  for (const product of products) {
    if (!product.sectionId) {
      continue;
    }

    const existing = map.get(product.sectionId);
    if (existing) {
      existing.push(product);
    } else {
      map.set(product.sectionId, [product]);
    }
  }

  return map;
}

function mapQuantities(lines: CartLine[]) {
  const map = new Map<string, number>();

  for (const line of lines) {
    map.set(line.productId, line.quantity);
  }

  return map;
}

function enrichCartLines(lines: CartLine[], products: PublicMenuProduct[]): CartLine[] {
  const productsById = new Map(products.map((product) => [product.id, product]));

  return lines.map((line) => {
    const product = productsById.get(line.productId);

    if (!product) {
      return line;
    }

    return {
      ...line,
      name: product.name,
      unitPrice: product.originalPrice,
      originalPrice: product.originalPrice,
      discountedUnitPrice: product.discountedPrice,
      promotionMinQuantity: product.promotionMinQuantity,
      discountPercentage: product.discountPercentage,
      promotionName: product.promotionName,
    };
  });
}

function cartTotals(lines: CartLine[]) {
  let itemCount = 0;
  let subtotal = 0;

  for (const line of lines) {
    itemCount += line.quantity;
    subtotal += lineSubtotal(line);
  }

  return { itemCount, subtotal };
}

function buildSelector(
  input: CreatePublicMenuFlowModelInput,
  enabledTypes: FulfillmentType[],
): PublicMenuFlowSelector {
  const isQrDineIn = input.variant === "dine-in" ? Boolean(input.qrToken) : false;

  return {
    visible: shouldShowFulfillmentSelector({ enabledTypes, isQrDineIn }),
    enabledTypes,
    current: input.variant,
  };
}

export function createPublicMenuFlowModel(
  input: CreatePublicMenuFlowModelInput,
): PublicMenuFlowModel {
  const productsBySection = groupProductsBySection(input.menu.products);
  const cartLines = enrichCartLines(input.cartLines, input.menu.products);
  const quantitiesByProductId = mapQuantities(cartLines);
  const { itemCount, subtotal } = cartTotals(cartLines);
  const enabledTypes = enabledFulfillmentTypes({
    businessType: input.menu.organization.businessType,
    orderingEnabled: input.menu.organization.orderingEnabled,
    deliveryEnabled: input.menu.organization.deliveryEnabled,
  });
  const selector = buildSelector(input, enabledTypes);

  const sectionsWithProducts = input.menu.sections.filter((section) => {
    const hasProducts = (productsBySection.get(section.id)?.length ?? 0) > 0;
    if (input.variant === "delivery") {
      return section.visible && hasProducts;
    }
    return hasProducts;
  });

  if (input.variant === "delivery") {
    const currency = input.menu.organization.currency ?? input.delivery.currency;
    const checkoutTarget = input.orgSlug ? `/m/${input.orgSlug}/delivery/checkout` : null;
    const minOrderMet = subtotal >= input.delivery.deliveryMinimumOrder;
    const hasPaymentMethod =
      input.delivery.payNowAvailable || input.delivery.cashOnDeliveryAvailable;
    const blockers: PublicMenuFlowCheckoutBlocker[] = [];

    if (!minOrderMet && input.delivery.deliveryMinimumOrder > 0) {
      blockers.push({ kind: "minimum-order", amount: input.delivery.deliveryMinimumOrder });
    }
    if (minOrderMet && !hasPaymentMethod) {
      blockers.push({ kind: "no-payment-method" });
    }

    return {
      variant: input.variant,
      organization: input.menu.organization,
      currency,
      productsBySection,
      quantitiesByProductId,
      sectionsForNav: sectionsWithProducts,
      sectionsForList: sectionsWithProducts,
      productOrderingEnabled: true,
      tableLabel: null,
      guestError: null,
      showScanToOrder: false,
      deliveryMinimumOrder: input.delivery.deliveryMinimumOrder,
      selector,
      cart: {
        lines: cartLines,
        itemCount,
        subtotal,
      },
      cartDrawer: {
        visible: itemCount > 0,
        checkoutTarget,
        canCheckout: Boolean(checkoutTarget) && itemCount > 0 && minOrderMet && hasPaymentMethod,
        rows: [
          { kind: "subtotal", amount: subtotal, emphasized: false },
          { kind: "delivery-fee", amount: input.delivery.deliveryFeeFlat, emphasized: false },
          { kind: "total", amount: subtotal + input.delivery.deliveryFeeFlat, emphasized: true },
        ],
        blockers,
      },
    };
  }

  const orderingEnabled = Boolean(input.menu.ordering?.enabled);
  const tableLabel = input.variant === "dine-in" ? input.tableLabel : null;
  const guestError = input.variant === "dine-in" ? input.guestError : null;
  const qrToken = input.variant === "dine-in" ? input.qrToken : null;
  const productOrderingEnabled =
    input.variant === "takeaway"
      ? orderingEnabled
      : Boolean(orderingEnabled && tableLabel && !guestError);
  const checkoutTarget = input.orgSlug ? `/m/${input.orgSlug}/checkout` : null;

  return {
    variant: input.variant,
    organization: input.menu.organization,
    currency: input.menu.organization.currency,
    productsBySection,
    quantitiesByProductId,
    sectionsForNav: sectionsWithProducts,
    sectionsForList: input.menu.sections,
    productOrderingEnabled,
    tableLabel,
    guestError,
    showScanToOrder:
      input.variant === "dine-in" && Boolean(orderingEnabled && !tableLabel && !qrToken),
    deliveryMinimumOrder: null,
    selector,
    cart: {
      lines: cartLines,
      itemCount,
      subtotal,
    },
    cartDrawer: {
      visible: productOrderingEnabled && itemCount > 0,
      checkoutTarget,
      canCheckout: Boolean(checkoutTarget),
      rows: [{ kind: "subtotal", amount: subtotal, emphasized: true }],
      blockers: [],
    },
  };
}
